import { useState } from 'react';
import { Card, CardBody, Col, Form, FormGroup, Input, Label, Media, Row } from 'reactstrap'
import { Btn, H4, P } from '../../../../../AbstractElements'
import { dynamicImage } from '../../../../../Service'
import CommonCardHeader from '../../../../../CommonElements/CommonCardHeader/CommonCardHeader'
import { useAuth } from '../../../../../hooks/useAuth'
import UserService from '../../../../../Api/services/user.service'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

const EditMyProfile = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Obtener foto del usuario o usar imagen por defecto
  const defaultImage = dynamicImage("user/7.jpg");
  const userPhoto = (user?.photo_url && !imageError) ? user.photo_url : defaultImage;

  // Obtener nombre del usuario
  const userName = user?.name || user?.email || 'Usuario';
  const userRole = user?.role?.name || 'Usuario';

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setIsChangingPassword(true);
      await UserService.changeProfilePassword(passwordData.newPassword);
      toast.success('Contraseña actualizada exitosamente');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cambiar contraseña';
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen');
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 5MB');
      return;
    }

    try {
      const updatedUser = await UserService.updateProfileImage(file);
      toast.success('Imagen de perfil actualizada exitosamente');
      if (refreshUser) {
        await refreshUser();
      }
      setImageError(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al actualizar imagen';
      toast.error(errorMessage);
    }
  };

  return (
    <Col xl="4">
      <Card>
        <CommonCardHeader title={t("Mi Perfil")} tagClass='card-title mb-0' />
        <CardBody>
          <Row className="mb-2">
            <div className="profile-title">
              <Media>
                <div className="position-relative">
                  <img
                    className="img-70 rounded-circle"
                    alt={userName}
                    src={userPhoto}
                    onError={() => setImageError(true)}
                    style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                  />
                  <label
                    htmlFor="profile-image-upload"
                    className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle p-1 cursor-pointer"
                    style={{ cursor: 'pointer', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title={t("Cambiar imagen")}
                  >
                    <i className="fa fa-camera" style={{ fontSize: '12px' }}></i>
                  </label>
                  <input
                    id="profile-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>
                <Media body className="ms-3">
                  <H4 className="mb-1">{userName}</H4>
                  <P className="mb-0">{userRole}</P>
                </Media>
              </Media>
            </div>
          </Row>
          
          <Form onSubmit={handlePasswordChange}>
            <FormGroup>
              <Label>{t("Nueva Password")}</Label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder={t("Ingresa tu nueva contraseña")}
                required
                minLength={6}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("Confirmar Nueva Password")}</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder={t("Confirma tu nueva contraseña")}
                required
                minLength={6}
              />
            </FormGroup>
            <div className="form-footer">
              <Btn color="primary" block type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? t("Cambiando...") : t("Cambiar Contraseña")}
              </Btn>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Col>
  )
}

export default EditMyProfile